import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef } from 'react';
import { bind } from 'bind-event-listener';
import { createHook, createSelector, createStore } from 'react-sweet-state';
import { mapErrorToApiError } from '../api-error';
var store = createStore({
  initialState: {
    cache: {}
  },
  actions: {
    updateCacheEntry: function updateCacheEntry(key, value) {
      return function (_ref) {
        var _state$cache$key;
        var setState = _ref.setState,
          getState = _ref.getState;
        var state = getState();
        var currentCacheEntry = (_state$cache$key = state.cache[key]) !== null && _state$cache$key !== void 0 ? _state$cache$key : {
          data: null,
          error: null,
          isFetching: false
        };
        var newCacheEntry = _objectSpread(_objectSpread({}, currentCacheEntry), value);
        setState({
          cache: _objectSpread(_objectSpread({}, state.cache), {}, _defineProperty({}, key, newCacheEntry))
        });
      };
    },
    getCacheEntry: function getCacheEntry(key) {
      return function (_ref2) {
        var getState = _ref2.getState;
        var state = getState();
        return state.cache[key];
      };
    }
  }
});
var selector = createSelector(function (state) {
  return state.cache;
}, function (_, key) {
  return key;
}, function (cache, key) {
  var _cache$key;
  return (_cache$key = cache[key]) !== null && _cache$key !== void 0 ? _cache$key : {
    data: null,
    error: null,
    isFetching: false
  };
});
var useCacheEntry = createHook(store, {
  selector: selector
});
export var useQuery = function useQuery(_ref3) {
  var queryKeyParts = _ref3.queryKey,
    queryFn = _ref3.queryFn,
    onSuccess = _ref3.onSuccess,
    onError = _ref3.onError,
    _ref3$enabled = _ref3.enabled,
    enabled = _ref3$enabled === void 0 ? true : _ref3$enabled,
    _ref3$refetchOnWindow = _ref3.refetchOnWindowFocus,
    refetchOnWindowFocus = _ref3$refetchOnWindow === void 0 ? false : _ref3$refetchOnWindow;
  var queryKey = queryKeyParts.map(function (x) {
    return x === null || x === void 0 ? void 0 : x.toString();
  }).join('_');
  var queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  var onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  var onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  var _useCacheEntry = useCacheEntry(queryKey),
    _useCacheEntry2 = _slicedToArray(_useCacheEntry, 2),
    cache = _useCacheEntry2[0],
    _useCacheEntry2$ = _useCacheEntry2[1],
    updateCacheEntry = _useCacheEntry2$.updateCacheEntry,
    getCacheEntry = _useCacheEntry2$.getCacheEntry;
  var fetchData = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var _onSuccessRef$current, response, _onErrorRef$current, apiError;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          updateCacheEntry(queryKey, {
            isFetching: true
          });
          _context.prev = 1;
          _context.next = 4;
          return queryFnRef.current();
        case 4:
          response = _context.sent;
          updateCacheEntry(queryKey, {
            data: response
          });
          (_onSuccessRef$current = onSuccessRef.current) === null || _onSuccessRef$current === void 0 || _onSuccessRef$current.call(onSuccessRef, response);
          _context.next = 14;
          break;
        case 9:
          _context.prev = 9;
          _context.t0 = _context["catch"](1);
          apiError = mapErrorToApiError(_context.t0);
          updateCacheEntry(queryKey, {
            error: apiError
          });
          (_onErrorRef$current = onErrorRef.current) === null || _onErrorRef$current === void 0 || _onErrorRef$current.call(onErrorRef, apiError);
        case 14:
          _context.prev = 14;
          updateCacheEntry(queryKey, {
            isFetching: false
          });
          return _context.finish(14);
        case 17:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 9, 14, 17]]);
  })), [updateCacheEntry, queryKey]);
  useEffect(function () {
    if (enabled && refetchOnWindowFocus) {
      var unbind = bind(window, {
        type: 'focus',
        listener: function listener() {
          var _getCacheEntry;
          if (enabled && refetchOnWindowFocus && !((_getCacheEntry = getCacheEntry(queryKey)) !== null && _getCacheEntry !== void 0 && _getCacheEntry.isFetching)) {
            fetchData();
          }
        }
      });
      return unbind;
    }
  }, [enabled, fetchData, getCacheEntry, queryKey, refetchOnWindowFocus]);
  useEffect(function () {
    if (!getCacheEntry(queryKey) && enabled) {
      fetchData();
    }
  }, [queryKey, fetchData, enabled, getCacheEntry]);
  return {
    data: cache.data,
    error: cache.error,
    isFetching: cache.isFetching,
    refetch: fetchData
  };
};